import { FC, useCallback } from "react";
import { Stack, Switch, Tooltip } from "@mui/material";
import { useToggle } from "rooks";
import { Report } from "@/services/cloudchipr.api";
import { useAppAbility } from "@/services/permissions";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { toggleReportStatusThunk } from "@/store/reports/thunks/toggleReportStatusThunk";
import { toggleReportStatusLoadingSelector } from "@/store/reports/selector/toggleReportStatusLoadingSelector";

interface ReportsStatusSwitchProps {
  reportId: string;
  disabled?: boolean;
  status?: Report["status"];
}

export const ReportsStatusSwitch: FC<ReportsStatusSwitchProps> = ({
  status,
  disabled,
  reportId,
}) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotEditSchedule = cannot("edit", "report");

  const [checked, toggleChecked] = useToggle(status === "active");
  const loading = useAppSelector((state) =>
    toggleReportStatusLoadingSelector(state, reportId ?? ""),
  );

  const checkTooltipTitle = checked ? "Active" : "Inactive";

  const toggleHandler = useCallback(() => {
    toggleChecked();
    dispatch(toggleReportStatusThunk({ reportId, status }));
  }, [reportId, status, dispatch, toggleChecked]);

  return (
    <Tooltip
      arrow
      placement="top"
      title={disabled ? disabledTooltipTitle : checkTooltipTitle}
    >
      <Stack style={stackStyles}>
        <Switch
          size="small"
          checked={checked && !disabled}
          onChange={toggleHandler}
          disabled={canNotEditSchedule || loading || disabled}
          sx={{ alignSelf: "center" }}
        />
      </Stack>
    </Tooltip>
  );
};

const disabledTooltipTitle =
  "This subscription can’t be enabled because the source was deleted. Please update the source to turn notifications back on.";

const stackStyles = {
  height: 20,
  alignItems: "center",
  justifyContent: "center",
};
